import { createApi } from '@reduxjs/toolkit/query/react';
import { benderApiClient } from 'src/clients/api-client';
import { type Template } from '../types/template.types';
import {
  type BoxFolder,
  type CreateSyncTargetRequest,
  type SyncTargetDto,
  type SyncTargetListArgs,
  type SyncTargetListResponse,
  type UpdateSyncTargetRequest,
} from '../types/sync-target.types';
import { benderMetricsPublisher, METRIC } from 'src/utils/metrics';
import { createBaseQuery } from 'src/store/rtk-query-base';

interface Filters {
  publishers?: string[];
  supportedLocales?: string[];
  businessLine?: string[];
}

interface BenderTemplateRequestQueryParams {
  filters?: Filters;
}

const SYNC_TARGET_LIST_ID = 'LIST';

export const benderApi = createApi({
  reducerPath: 'benderApi',
  baseQuery: createBaseQuery(benderApiClient, {
    additionalErrorHandling: () => {
      benderMetricsPublisher.publishCounter(METRIC.Error, 1);
    },
  }),
  tagTypes: ['SyncTarget', 'BoxFolder'],
  endpoints: (builder) => ({
    getBenderTemplates: builder.query<Template[], BenderTemplateRequestQueryParams | void>({
      query: (params) => ({
        url: '/templates',
        method: 'GET',
        params,
      }),
    }),

    getSyncTargets: builder.query<SyncTargetListResponse, SyncTargetListArgs | void>({
      query: (args) => ({
        url: '/sync-targets',
        method: 'GET',
        params: { page: args?.page, pageSize: args?.pageSize },
      }),
      providesTags: (result) => [
        { type: 'SyncTarget' as const, id: SYNC_TARGET_LIST_ID },
        ...(result?.data ?? []).map((target) => ({ type: 'SyncTarget' as const, id: target.syncTargetId })),
      ],
    }),

    createSyncTarget: builder.mutation<SyncTargetDto, CreateSyncTargetRequest>({
      query: (body) => ({
        url: '/sync-targets',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'SyncTarget', id: SYNC_TARGET_LIST_ID }],
    }),

    updateSyncTarget: builder.mutation<SyncTargetDto, UpdateSyncTargetRequest>({
      query: ({ syncTargetId, excludedFolderIds, excludedPathnames }) => ({
        url: `/sync-targets/${syncTargetId}`,
        method: 'PATCH',
        body: { excludedFolderIds, excludedPathnames },
      }),
      invalidatesTags: (result, error, { syncTargetId }) => [
        { type: 'SyncTarget', id: syncTargetId },
        { type: 'SyncTarget', id: SYNC_TARGET_LIST_ID },
      ],
    }),

    getBoxFolder: builder.query<BoxFolder, { folderId: string }>({
      query: ({ folderId }) => ({
        url: '/box',
        method: 'GET',
        params: { folderId },
      }),
      // Never cache. Box access is a live collaboration grant that can be revoked,
      // and a cached green check on a folder that has since lost access is exactly
      // the failure this probe exists to prevent.
      keepUnusedDataFor: 0,
      providesTags: (result, error, args) => [{ type: 'BoxFolder', id: args.folderId }],
    }),

    /** Responds 202 with no body — the enumeration has been requested, not performed. */
    refreshSyncTarget: builder.mutation<void, { syncTargetId: string }>({
      query: ({ syncTargetId }) => ({
        url: `/sync-targets/${syncTargetId}/refresh`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, args) => [
        { type: 'SyncTarget', id: args.syncTargetId },
        { type: 'SyncTarget', id: SYNC_TARGET_LIST_ID },
      ],
    }),
  }),
});

export const {
  useGetBenderTemplatesQuery,
  useGetSyncTargetsQuery,
  useCreateSyncTargetMutation,
  useUpdateSyncTargetMutation,
  useGetBoxFolderQuery,
  useRefreshSyncTargetMutation,
} = benderApi;
